import { Injectable } from '@angular/core'
import { EventModel, EventModelDto, IEvent } from '../models/event.model'
import { AngularFireDatabase } from '@angular/fire/database'
import { AngularFireList } from 'angularfire2/database'
import { Observable, throwError } from 'rxjs'
import { map, take, catchError } from 'rxjs/operators'
import { EmailService } from './email.service'
import { Email } from '@shared/const/email/email.const'
import { AttendeesService } from './attendees.service'
import { AttendeeModel } from '../models/attendee.model'
import { UserService } from './user.service'
import {
  getUpdateLogDetails,
  getCreateLogDetails,
  getDeleteLogDetails,
  LogAction,
} from '../decorator/log-action.decorator'
import { HistoryService } from '../services/history/history.service'
import { AuthService } from './auth.service'

@Injectable({
  providedIn: 'root',
})
export class EventService {
  constructor(
    private db: AngularFireDatabase,
    public historyService: HistoryService,
    public authService: AuthService,
    private emailService: EmailService,
    private userService: UserService,
    private attendeesService: AttendeesService
  ) {}

  async update(event: IEvent, eventId: string, oldOrganiserId: string, newOrganiserId: string): Promise<void> {
    if (oldOrganiserId === newOrganiserId) {
      await this.updateEvent(event, eventId, newOrganiserId)
      return
    }

    return this.checkEventExists(eventId, newOrganiserId).then(exists => {
      if (exists) {
        throw new Error('Event already exists under the new organiser')
      }
      return this.moveEvent(event, eventId, oldOrganiserId, newOrganiserId)
    })
  }

  @LogAction((args, result) => (args[2] ? 'approve_event' : 'disapprove_event'), getUpdateLogDetails)
  async approve(
    event: IEvent,
    eventId: string,
    isApproved: boolean,
    organiserId: string
  ): Promise<{ previousData: EventModel | null }> {
    const previousData = await this.getPreviousData(organiserId, eventId)
    await this.db.object<EventModel>(`/events/${organiserId}/${eventId}/`).update({
      isApproved: isApproved,
    })
    return { previousData }
  }

  giveReasonOfDelete(reasonOfDelete: string, eventId: string, organiserId: string): Promise<void> {
    return this.db.object<EventModel>(`/events/${organiserId}/${eventId}/`).update({
      reasonOfDelete: reasonOfDelete,
    })
  }

  @LogAction('create_event', (args, result) => getCreateLogDetails(args, result))
  create(event: IEvent, userId: string, userName: string) {
    const eventRef = this.db.list(`/events/${userId}`).push(event)
    this.emailService.send(
      Email.ADMIN.EMAIL,
      Email.ADMIN.EMAIL,
      Email.MESSAGE.NEW_EVENT_CREATED.SUBJECT,
      this.getNewEventCreatedEmailWithParams(event.name, userName)
    )
    return eventRef
  }

  public async duplicate(event: EventModel): Promise<void> {
    const organiser = await this.userService.get(event.organiserId).pipe(take(1)).toPromise()
    if (!organiser) {
      return
    }
    const duplicatedEvent: IEvent = {
      name: event.name + ' (Másolat)',
      description: event.description,
      fromDateTime: event.fromDateTime,
      toDateTime: event.toDateTime,
      deadlineDateTime: event.deadlineDateTime,
      city: event.city,
      address: event.address,
      maxNumberOfAttendees: event.maxNumberOfAttendees,
      organiserId: event.organiserId,
      money: event.money,
      cabbage: event.cabbage,
      salt: event.salt,
      isApproved: false,
    }
    this.create(duplicatedEvent, event.organiserId, organiser.name)
    return
  }

  getNewEventCreatedEmailWithParams(eventName: string, userName: string): string {
    return Email.MESSAGE.NEW_EVENT_CREATED.HTML.replace('{eventName}', eventName).replace('{organization}', userName)
  }

  get(eventId: string, organiserId: string): Observable<EventModel> {
    if (!organiserId || !eventId) {
      return throwError('Organizer ID and Event ID are required.')
    }

    return this.db
      .object<EventModel>(`/events/${organiserId}/${eventId}/`)
      .snapshotChanges()
      .pipe(
        map(eventSnapshots => {
          const data = eventSnapshots.payload.val()
          const id = eventSnapshots.payload.key
          if (data) {
            return new EventModel({ id, ...data })
          } else {
            console.error('Event not found for path:', `/events/${organiserId}/${eventId}/`)
            throw new Error('Event not found.')
          }
        }),
        catchError(error => {
          console.error('Error in snapshotChanges:', error)
          throw error
        })
      )
  }

  getAll(): AngularFireList<Map<string, EventModelDto>> {
    return this.db.list<Map<string, EventModelDto>>(`/events`)
  }

  getAllByUserId(userId: string): AngularFireList<EventModelDto> {
    return this.db.list<EventModelDto>(`/events/${userId}`)
  }

  getAllAsAdmin(): Observable<EventModel[]> {
    let eventArray = []
    return this.getAll()
      .snapshotChanges()
      .pipe(
        map(eventSnapshots => {
          eventSnapshots.map(eventSnapshot => {
            const eventPayload = eventSnapshot.payload
            eventArray.push(
              ...Object.keys(eventPayload.val()).map((key: string) => ({
                id: key,
                organiserId: eventPayload.key,
                ...new EventModelDto(eventPayload.val()[key]),
              }))
            )
          })
          const tempArray = eventArray
          eventArray = []
          return tempArray
        })
      )
  }

  getAllAsOrganizer(userId: string): Observable<EventModel[]> {
    let eventArray = []
    return this.getAllByUserId(userId)
      .snapshotChanges()
      .pipe(
        map(eventSnapshots => {
          eventArray = eventSnapshots.map(eventSnapshot => ({
            id: eventSnapshot.payload.key,
            organiserId: userId,
            ...new EventModelDto(eventSnapshot.payload.val()),
          }))
          const tempArray = eventArray
          eventArray = []
          return tempArray
        })
      )
  }

  @LogAction('delete_event', (args, result) => getDeleteLogDetails(args, result))
  async delete(
    eventId: string,
    event: EventModel,
    userId: string,
    shouldNotifyAttendees: boolean
  ): Promise<{ previousData: EventModel | null }> {
    const previousData = await this.getPreviousData(userId, eventId)
    if (!shouldNotifyAttendees) {
      await this.db.list(`/events/${userId}`).remove(event.id)
      return { previousData }
    }
    try {
      const attendees = await this.attendeesService.getAllByEventId(event.id).pipe(take(1)).toPromise()
      const organiser = await this.userService.get(event.organiserId).pipe(take(1)).toPromise()

      attendees.forEach((attendee: AttendeeModel) => {
        try {
          this.emailService.send(
            Email.ADMIN.EMAIL,
            attendee.email,
            Email.MESSAGE.DELETED_EVENT.SUBJECT,
            this.getDeletedEventWithParams(event, organiser.name, attendee.name)
          )
        } catch (emailError) {
          console.error(`Error sending email to attendee ${attendee.email}:`, emailError)
        }
      })
      try {
        this.emailService.send(
          Email.ADMIN.EMAIL,
          organiser.email,
          Email.MESSAGE.DELETED_EVENT_ORGANIZATION.SUBJECT,
          this.getDeletedEventForOrganizationWithParams(event, organiser.name)
        )
      } catch (emailError) {
        console.error(`Error sending email to organiser ${organiser.email}:`, emailError)
      }
      await this.db.list(`/events/${userId}`).remove(event.id)
      return { previousData }
    } catch (error) {
      console.error('Error deleting event:', error)
      return null
    }
  }

  private getDeletedEventWithParams(event: EventModel, organizationName: string, attendeeName: string): string {
    return Email.MESSAGE.DELETED_EVENT.HTML.replace('{eventName}', event.name)
      .replace('{attendeeName}', attendeeName)
      .replace('{eventCity}', event.city)
      .replace('{eventAddress}', event.address)
      .replace('{eventOrganizer}', organizationName)
      .replace('{eventDateTimeFrom}', event.fromDateTime)
      .replace('{eventDateTimeTo}', event.toDateTime)
      .replace('{reason}', event.reasonOfDelete)
  }

  private getDeletedEventForOrganizationWithParams(event: EventModel, organizationName: string): string {
    return Email.MESSAGE.DELETED_EVENT_ORGANIZATION.HTML.replace('{eventName}', event.name)
      .replace('{eventCity}', event.city)
      .replace('{eventAddress}', event.address)
      .replace('{eventOrganizer}', organizationName)
      .replace('{eventDateTimeFrom}', event.fromDateTime)
      .replace('{eventDateTimeTo}', event.toDateTime)
      .replace('{reason}', event.reasonOfDelete)
  }

  @LogAction('update_event', (args, result) => getUpdateLogDetails(args, result))
  private async updateEvent(
    event: IEvent,
    eventId: string,
    organiserId: string
  ): Promise<{ previousData: EventModel | null }> {
    const previousData = await this.getPreviousData(organiserId, eventId)
    await this.db.object<EventModel>(`/events/${organiserId}/${eventId}/`).update({
      name: event.name,
      description: event.description,
      fromDateTime: event.fromDateTime,
      toDateTime: event.toDateTime,
      deadlineDateTime: event.deadlineDateTime,
      city: event.city,
      address: event.address,
      maxNumberOfAttendees: event.maxNumberOfAttendees,
      organiserId: organiserId,
      money: event.money,
      cabbage: event.cabbage,
      salt: event.salt,
    })
    return { previousData }
  }

  private checkEventExists(eventId: string, organiserId: string): Promise<boolean> {
    return this.db
      .object<EventModel>(`/events/${organiserId}/${eventId}/`)
      .snapshotChanges()
      .pipe(take(1))
      .toPromise()
      .then(snapshot => snapshot.payload.exists())
  }

  private moveEvent(event: IEvent, eventId: string, oldOrganiserId: string, newOrganiserId: string): Promise<void> {
    const deleteOldEvent = this.db.object<EventModel>(`/events/${oldOrganiserId}/${eventId}/`).remove()
    event.organiserId = newOrganiserId
    const createNewEvent = this.db.object<IEvent>(`/events/${newOrganiserId}/${eventId}/`).set(event)
    return Promise.all([deleteOldEvent, createNewEvent]).then(() => {})
  }

  private async getPreviousData(organiserId: string, id: string) {
    const snapshot = await this.db.object<EventModel>(`/events/${organiserId}/${id}/`).query.once('value')
    const previousData = snapshot.exists() ? snapshot.val() : null
    return previousData
  }

  public getFutureApprovedEvents(): Observable<EventModel[]> {
    return this.getAllAsAdmin().pipe(
      map((allEvents: EventModel[]) => {
        return allEvents.filter(event => {
          const toDateTime = new Date(event.toDateTime)
          toDateTime.setHours(toDateTime.getHours() + 24)
          return toDateTime.getTime() > Date.now() && event.isApproved
        })
      })
    )
  }
}
